import { useState } from 'react'
import {
  UsersHeader,
  UsersTable,
  type UserData,
  type GroupOption,
  type CompanyOption,
  type CreateUserInput,
  type CreateGroupOwnerInput,
  type CreateSuperAdminInput,
  type CreateComplianceOfficerInput,
  type UserOption,
} from '@valiant/ui-shared'
import {
  useGetUsersQuery,
  useGetGroupsQuery,
  useGetCompaniesQuery,
  useCreateUserMutation,
  useCreateGroupOwnerMutation,
  useCreateSuperAdminMutation,
  useCreateComplianceOfficerMutation,
  useAddUserToGroupMutation,
  useAssignUserToCompaniesMutation,
  type UserRole,
  GroupStatus,
  useRemoveUserFromGroupMutation,
  useRemoveUserFromCompanyMutation,
} from '@/generated/graphql'
import { useDebounce } from '@valiant/ui-shared'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/AuthProvider'

interface UsersContentProps {
  searchTerm?: string
  selectedRole: UserRole | 'all'
  onRoleChange: (role: UserRole | 'all') => void
  groupId?: string
  companyId?: string
  adminOnly?: boolean
  availableRoleFilters?: string[]
  allowedRoles?: UserRole[]
  showUnassignUserFromGroup?: boolean
}

export function UsersContent({
  searchTerm,
  selectedRole,
  onRoleChange,
  groupId,
  companyId,
  adminOnly = false,
  availableRoleFilters = ['TRADER', 'GROUP_OWNER', 'SUPER_ADMIN', 'COMPLIANCE'],
  allowedRoles,
  showUnassignUserFromGroup,
}: UsersContentProps) {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [cursor, setCursor] = useState<{ after?: string; before?: string }>({})
  const [modalHandlers, setModalHandlers] = useState<{
    handleViewPermissionsClick: () => void
    handleCreateClick: () => void
    handleAddExistingClick: () => void
  } | null>(null)

  // Debounce search terms
  const debouncedSearchTerm = useDebounce(searchTerm)
  const debouncedUserSearchTerm = useDebounce(userSearchTerm)

  // Build filter for main users query
  const filter = {
    ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
    ...(groupId && { groupId }),
    ...(companyId && { companyId }),
    ...(selectedRole !== 'all' && { role: selectedRole }),
    ...(adminOnly && { adminOnly }),
  }

  // Reset cursor when filter changes
  const filterKey = JSON.stringify(filter)
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setCursor({})
  }

  // GraphQL queries
  const [{ data: usersData, fetching: usersLoading }] = useGetUsersQuery({
    variables: {
      filter: Object.keys(filter).length > 0 ? filter : undefined,
      pagination: { first: 20, after: cursor.after, before: cursor.before },
    },
  })

  const [{ data: groupsData }] = useGetGroupsQuery({
    variables: {
      filter: { status: GroupStatus.Active },
    },
  })
  const [{ data: companiesData }] = useGetCompaniesQuery()

  // Query for available users (for adding existing users)
  const [{ data: availableUsersData, fetching: availableUsersLoading }] = useGetUsersQuery({
    variables: {
      filter: {
        ...(debouncedUserSearchTerm && { search: debouncedUserSearchTerm }),
        ...(groupId && !companyId && { excludeGroupId: groupId }),
      },
      pagination: {
        first: 100,
      },
    },
  })

  // Mutations
  const [, createUser] = useCreateUserMutation()
  const [, createGroupOwner] = useCreateGroupOwnerMutation()
  const [, createSuperAdmin] = useCreateSuperAdminMutation()
  const [, createComplianceOfficer] = useCreateComplianceOfficerMutation()
  const [, addUserToGroup] = useAddUserToGroupMutation()
  const [, assignUserToCompanies] = useAssignUserToCompaniesMutation()
  const [, removeUserFromGroup] = useRemoveUserFromGroupMutation()
  const [, removeUserFromCompany] = useRemoveUserFromCompanyMutation()

  // Data mapping
  const users: UserData[] = (usersData?.getUsers?.nodes || []).map((user) => ({
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role as UserRole,
    isWhitelisted: user.isWhitelisted,
    isActive: user.isActive,
    lastActive: user.lastActive || '',
    companiesCount: user.companiesCount,
    groupsCount: user.groupsCount,
  }))

  const groups: GroupOption[] = (groupsData?.getGroups || []).map((group) => ({
    groupId: group.groupId,
    name: group.name,
    status: group.status as 'ACTIVE' | 'DEACTIVATED',
  }))

  const companies: CompanyOption[] = (companiesData?.getCompanies || []).map((company) => ({
    id: company.id,
    company: company.company,
    group: company.group,
    groupId: company.groupId,
  }))

  const availableUsers: UserOption[] = (availableUsersData?.getUsers.nodes || [])
    .filter((user) => {
      // For company pages, only show TRADER users
      // For group pages, show both TRADER and GROUP_OWNER roles
      // SUPER_ADMIN and COMPLIANCE are MSB-level roles assigned to all groups/companies
      if (allowedRoles) {
        if (allowedRoles.includes(user.role)) {
          return true
        } else {
          return false
        }
      } else {
        if (companyId) {
          return user.role === 'TRADER'
        }
        if (groupId) {
          return user.role === 'TRADER' || user.role === 'GROUP_OWNER'
        }
        return true
      }
      return false
    })
    .map((user) => ({
      id: user.id,
      username: user.username,
      email: user.email,
    }))

  // Callback implementations
  const handleManageUser = (user: UserData) => {
    if (user.role === 'TRADER') {
      navigate(`/users/${user.id}`)
    } else if (user.role === 'GROUP_OWNER') {
      navigate(`/group-owner/${user.id}`)
    } else if (user.role === 'SUPER_ADMIN') {
      navigate(`/super-admin/${user.id}`)
    } else if (user.role === 'COMPLIANCE') {
      navigate(`/compliance-officer/${user.id}`)
    }
  }

  const handleCreateUser = async (input: CreateUserInput) => {
    try {
      const result = await createUser({
        input: {
          username: input.username,
          email: input.email,
          isWhitelisted: input.isWhitelisted,
          groupIds: input.groupIds,
          companyIds: input.companyIds,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(
        `User "${input.username}" created successfully and assigned to ${input.groupIds.length} group(s) and ${input.companyIds.length} company(ies)`,
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to create user', { description: errorMessage })
      throw error
    }
  }

  const handleCreateGroupOwner = async (input: CreateGroupOwnerInput) => {
    try {
      const result = await createGroupOwner({
        input: {
          username: input.username,
          email: input.email,
          groupIds: input.groupIds,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Group Owner "${input.username}" created successfully`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to create group owner', { description: errorMessage })
      throw error
    }
  }

  const handleCreateSuperAdmin = async (input: CreateSuperAdminInput) => {
    try {
      const result = await createSuperAdmin({
        input: {
          username: input.username,
          email: input.email,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Super Admin "${input.username}" created successfully`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to create super admin', { description: errorMessage })
      throw error
    }
  }

  const handleCreateComplianceOfficer = async (input: CreateComplianceOfficerInput) => {
    try {
      const result = await createComplianceOfficer({
        input: {
          username: input.username,
          email: input.email,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Compliance Officer "${input.username}" created successfully`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to create compliance officer', { description: errorMessage })
      throw error
    }
  }

  const handleAddUserToGroup = async (userId: string, targetGroupId: string, asAdmin: boolean) => {
    try {
      const result = await addUserToGroup({
        input: {
          userId,
          groupId: targetGroupId,
          asAdmin,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`User added ${asAdmin ? 'as group owner' : 'to group'} successfully!`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to add user ${asAdmin ? 'as group owner' : 'to group'}`, {
        description: errorMessage,
      })
      throw error
    }
  }

  const handleAddUserToCompany = async (userId: string, targetCompanyId: string) => {
    if (!targetCompanyId) return

    try {
      const result = await assignUserToCompanies({
        input: {
          userId,
          companyIds: [targetCompanyId],
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success('User successfully assigned to company!')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to assign user to company', {
        description: errorMessage,
      })
      throw error
    }
  }

  const handleValidationError = (_field: string, _message: string) => {
    // Validation errors are handled by the component internally
    // This callback is for additional error handling if needed
  }

  const handleUnassignUserFromGroup = showUnassignUserFromGroup
    ? async (userId: string, groupId: string) => {
        try {
          const result = await removeUserFromGroup({
            input: {
              userId,
              groupId,
            },
          })

          if (result.error) {
            throw new Error(result.error.message)
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'An unexpected error occurred'
          toast.error('Failed to unassign user from group', { description: errorMessage })
          throw error
        }
      }
    : undefined

  const handleUnassignUserFromCompany =
    companyId && !isComplianceUser
      ? async (userId: string, targetCompanyId: string) => {
          try {
            const result = await removeUserFromCompany({
              input: {
                userId,
                companyId: targetCompanyId,
              },
            })

            if (result.error) {
              throw new Error(result.error.message)
            }

            toast.success('User successfully unassigned from company!')
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'An unexpected error occurred'
            toast.error('Failed to unassign user from company', { description: errorMessage })
            throw error
          }
        }
      : undefined

  return (
    <div className="space-y-4">
      <UsersHeader
        title={companyId ? 'Company Users' : adminOnly ? 'Group Owners' : 'Users'}
        selectedRole={selectedRole}
        onRoleChange={(role) => onRoleChange(role as UserRole | 'all')}
        showViewPermissionsButton={true}
        showCreateButton={!isComplianceUser}
        showAddExistingButton={!!groupId && !isComplianceUser}
        onViewPermissionsClick={modalHandlers?.handleViewPermissionsClick}
        onCreateClick={modalHandlers?.handleCreateClick}
        onAddExistingClick={modalHandlers?.handleAddExistingClick}
        availableRoleFilters={availableRoleFilters}
        allowedRoles={allowedRoles}
      />

      <UsersTable
        users={users}
        loading={usersLoading}
        showViewPermissionsButton={true}
        showCreateButton={!isComplianceUser}
        showAddExistingButton={!!groupId && !isComplianceUser}
        showAllRolesInPermissions={true}
        onManageUser={handleManageUser}
        groupId={groupId}
        companyId={companyId}
        adminOnly={adminOnly}
        groups={groups}
        companies={companies}
        availableUsers={availableUsers.filter((user) => !users.some((u) => u.id === user.id))}
        usersLoading={availableUsersLoading}
        onCreateUser={handleCreateUser}
        onCreateGroupOwner={handleCreateGroupOwner}
        onCreateSuperAdmin={handleCreateSuperAdmin}
        onCreateComplianceOfficer={handleCreateComplianceOfficer}
        onUnassignUserFromGroup={handleUnassignUserFromGroup}
        onUnassignUserFromCompany={handleUnassignUserFromCompany}
        onAddUserToGroup={handleAddUserToGroup}
        onAddUserToCompany={handleAddUserToCompany}
        onUserSearchChange={setUserSearchTerm}
        onValidationError={handleValidationError}
        onModalHandlersReady={setModalHandlers}
        allowedRoles={allowedRoles}
        pageInfo={usersData?.getUsers?.pageInfo}
        totalCount={usersData?.getUsers?.totalCount}
        showPagination={true}
        onNextPage={(endCursor) => setCursor({ after: endCursor })}
        onPreviousPage={(startCursor) => setCursor({ before: startCursor })}
      />
    </div>
  )
}
